// React Dependencies
import React, { useRef, useEffect, useReducer, useState } from 'react';

// External Dependencies
import moment from 'moment';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { TextField, Checkbox, FormControlLabel, RadioGroup, Radio, Button, Typography, Dialog } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

// Internal Dependencies
import { Status, Error } from '../../../common/constants';
import ApiService from '../../../services.js/apiService';
import OverLoading from '../../global/OverLoading';
import { IsEmpty } from '../../../common/checks';
import Photo from './Photo';
import MaterialDatePicker from '../../global/form/MaterialDatePicker';
import MText from '../../common/MText';
import Colours from '../../../styles/colours';

const defaultError = { isError: false, helperText: '' };
const useStyles = makeStyles(() => ({
  stepperRoot: {
    width:      '100%',
    padding:    15,
    paddingTop: 20,
  },
  saveButton: {
    width: '100%',

    '@media (min-width: 992px)': {
      width: '50%',
    },
  },
}));

const defaultTextInputs = {
  loyaltyCardName:       '',
  code:                  '',
  termsAndConditions:    '',
  validationDays:        null,
  status:                Status.Draft,
  stampTextColour:       null,
  isAuthRequiredToIssue: false,  // Ensure this is part of the default state
};

const defaultConfirmDialogState = {
  open:      false,
  confirmed: false,
  context:   null, // either updating to Live or Deactivated
};

const confirmationImpactTexts = {
  [Status.Live]: [
    'Loyalty Card will be publicly available',
    'Number of stamps in the card cannot be modified',
    'Card’s validity period cannot be updated',
    'Status cannot be updated back to ’Draft’',
  ],
  [Status.Deactivated]: [
    'The loyalty card will no longer be publicly available',
    'All information of the loyalty card cannot be modified',
    'Status cannot be updated back to Live',
  ],
};

const ValidityBy = {
  Days: 'Days',
  Date: 'Date',
};

const ValidityType = {
  ExpiryDate:     'expiryDate',
  ValidityInDays: 'validityInDays',
};

const LoyaltyCardInfo = ({ match }) => {
  const classes = useStyles();

  const daysInputRef = useRef();
  const labelTextSize = 13;

  const [image, setImage] = useState(null);
  const [initialStatus, setInitialStatus] = useState(null);

  const isDeactivated = initialStatus === Status.Deactivated;
  const isDraft = initialStatus === Status.Draft;
  const isLive = initialStatus === Status.Live;

  const { pageId, loyaltyCardId } = match.params;

  const [inputs, setInputs] = useReducer((state, action) => ({ ...state, ...action }), defaultTextInputs);
  const [confirmDialog, setConfirmDialog] = useReducer((state, action) => ({ ...state, ...action }), defaultConfirmDialogState);
  const [inputError, setInputError] = useState({ validityInDays: defaultError });
  const [loading, setLoading] = useState(true);

  const [expiryDateField, setExpiryDateField] = useState(null);
  const [validityBy, setValidityBy] = useState(ValidityBy.Days);

  const checkStatusTransitionDisabled = (status) => {
    if (isLive) {
      return status === Status.Draft;
    } else if (isDraft) {
      return status === Status.Deactivated;
    } else if (isDeactivated) {
      return [Status.Live, Status.Draft].includes(status);
    }
    return false;
  };

  const getImageSize = async (src) => {
    try {
      const img = new Image();

      return await new Promise((resolve, reject) => {
        img.onload = () => {
          resolve({ width: img.naturalWidth, height: img.naturalHeight });
        };
        img.onerror = () => {
          reject(new Error(`Failed to load image from ${src}`));
        };
        img.src = src;
      });
    } catch (err) {
      console.log('Error in getImageSize: ', err);
    }
  };

  const validateValidity = (value) => !(value && Number(value) < 1);

  const validateImage = async (imageObj) => {
    const imageSize = await getImageSize(imageObj.data);
    if (imageSize.width <= 750) {
      toast.error('The image width should not be less than 750 pixels');
      return false;
    }

    return true;
  };

  const handleInput = (e) => {
    const { name, value, type, checked } = e.target;
    const inputValue = type === 'checkbox' ? checked : value;
    const parsedValue = name === ValidityType.ValidityInDays && !IsEmpty(inputValue) ? Number(inputValue) : inputValue;

    if (name === ValidityType.ValidityInDays) {
      const isValid = parsedValue >= 0; // Ensure no negative values
      setInputError({
        ...inputError,
        validityInDays: (isValid && validateValidity(parsedValue) ? defaultError : { isError: true, helperText: Error.MinValidityPeriod  }),
      });
    }

    if (name !== ValidityType.ValidityInDays || parsedValue >= 0) {
      setInputs({ [name]: parsedValue });
    }
  };

  const getLoyaltyCardDetails = async () => {
    try {
      const res = await ApiService.getLoyaltyCard(pageId, loyaltyCardId);
      const { expiryDate, terms, validityInDays, isAuthRequiredToIssue, status, name, mainImage, stampTextColour, code } = res;

      const formattedExpiryDate = !IsEmpty(expiryDate) ? moment(expiryDate).utcOffset('+08:00', false).format() : null;

      if (IsEmpty(validityInDays) && IsEmpty(expiryDate)) {
        setValidityBy(ValidityBy.Days);
      } else {
        setValidityBy(IsEmpty(validityInDays) ? ValidityBy.Date : ValidityBy.Days);
      }

      setInputs({
        terms,
        validityInDays: (validityInDays !== null ? Number(validityInDays) : null),
        expiryDate:     formattedExpiryDate,
        isAuthRequiredToIssue,
        status,
        name,
        stampTextColour,
        code,
      });

      // Get only the date parts as it will give a different result in different timezone if we include the timezone offset
      // (e.g. in GMT+09:00, it will be interpreted as nextday)
      setExpiryDateField(!IsEmpty(formattedExpiryDate) ? formattedExpiryDate.split('T')[0] : null);
      setImage(mainImage);
      setInitialStatus(status);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const closeDialog = () => {
    setConfirmDialog({ open: false, confirmed: false });
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      const { name, terms, validityInDays, expiryDate, status, stampTextColour, code } = await ApiService.updateLoyaltyCard(
        pageId,
        loyaltyCardId,
        inputs,
      );

      const formattedExpiryDate = !IsEmpty(expiryDate) ? moment(expiryDate).utcOffset('+08:00', false).format() : null;

      setInputs({
        name,
        terms,
        expiryDate:     formattedExpiryDate,
        validityInDays: validityInDays !== null ? Number(validityInDays) : null,
        status,
        stampTextColour,
        code,
      });
      daysInputRef.current.value = validityInDays;
      // ExpiriDateField state are to hold previous data on expiryDate when user toggle between expiryDate and validityInDays
      setExpiryDateField(!IsEmpty(formattedExpiryDate) ? formattedExpiryDate.split('T')[0] : null);
      setInitialStatus(status);

      closeDialog();

      toast.success('Loyalty card updated successfully');
    } catch (err) {
      console.log(err);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMainImage = async (imageObj) => {
    const formData = new FormData();
    formData.append('image', imageObj.file);

    try {
      setLoading(true);
      const isValidated = await validateImage(imageObj);

      if (isValidated) {
        const imageUrl = await ApiService.uploadLoyaltyCardMedia(pageId, loyaltyCardId, formData, imageObj.file);
        setImage(imageUrl);
        toast.success('Main image has been updated');
      }
    } catch (err) {
      console.log(err);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getLoyaltyCardDetails();
  }, []);

  useEffect(() => {
    if (validityBy === ValidityBy.Days) {
      handleInput({ target: { name: ValidityType.ExpiryDate, value: null } });
      handleInput({ target: { name: ValidityType.ValidityInDays, value: daysInputRef.current.valueAsNumber } });
    } else if (!IsEmpty(expiryDateField)) {
      handleInput({ target: { name: ValidityType.ExpiryDate, value: moment(expiryDateField).utcOffset('+08:00', false).endOf('day').format() } });
      handleInput({ target: { name: ValidityType.ValidityInDays, value: null } });
    } else {
      const formattedDate = moment().utcOffset('+08:00', true).add(1, 'year').endOf('day')
        .format();
      handleInput({ target: { name: ValidityType.ExpiryDate, value: formattedDate } });
      handleInput({ target: { name: ValidityType.ValidityInDays, value: null } });
      setExpiryDateField(formattedDate.split('T')[0]);
    }
  }, [validityBy]);

  return (
    <>
      {loading && <OverLoading /> }
      <div className="container-fluid">
        <div className="row mb-3">
          <div className="col-12 col-xl-6 pl-0">
            <Typography
              style={{    fontSize: 12,
                color:    Colours.DarkGray }}
              className="mb-2"
            >
              Loyalty Card ID:
              {' '}
              {loyaltyCardId}
            </Typography>
            <Typography style={{ fontWeight: 'bold', fontSize: 16 }}>
              Card Info
            </Typography>
          </div>
        </div>

        <form className="w-100">
          <div className="row mb-4">
            <div className="col-12 col-xl-6 pl-0">
              <Typography className="mb-2" style={{ fontSize: 13 }}>Loyalty Card Image</Typography>
              <div className="mb-3">
                <Photo
                  title="+ Click to upload"
                  image={image}
                  handleImage={handleMainImage}
                  disabled={isDeactivated}
                />
              </div>
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-12 col-xl-6 pl-0">
              <Typography style={{ fontSize: 13 }} className="mb-1">
                Loyalty Card Name
              </Typography>
              <div>
                <TextField
                  name="name"
                  value={inputs.name}
                  disabled={isDeactivated}
                  variant="outlined"
                  onChange={handleInput}
                  inputProps={{ style: { fontSize: 13 } }}
                  size="small"
                  className="w-100"
                />
              </div>
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-12 col-xl-6 pl-0">
              <Typography style={{ fontSize: 13 }} className="mb-1">
                Code
              </Typography>
              <div>
                <TextField
                  name="code"
                  value={inputs.code}
                  placeholder="Code"
                  disabled={isDeactivated}
                  variant="outlined"
                  onChange={handleInput}
                  inputProps={{ style: { fontSize: 13 } }}
                  size="small"
                  className="w-100"
                />
              </div>
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-12 col-xl-6 pl-0">
              <Typography style={{ fontSize: 13 }} className="mb-1">
                Terms & Conditions
              </Typography>
              <div>
                <TextField
                  size="small"
                  name="terms"
                  variant="outlined"
                  disabled={isDeactivated}
                  multiline
                  inputProps={{ style: { fontSize: 13 } }}
                  rows={5}
                  className="w-100"
                  value={inputs.terms}
                  onChange={handleInput}
                />
              </div>
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-12 col-xl-12 pl-0">

              <MText size={labelTextSize} className="mb-1">
                Card&rsquo;s Validity or Expiry Date
              </MText>
              <RadioGroup row value={validityBy} onChange={(e) => setValidityBy(e.target.value)}>
                <div className="col-md-6 my-1 mb-3">
                  <div className="d-flex">
                    <FormControlLabel
                      value={ValidityBy.Days}
                      disabled={isLive || isDeactivated}
                      control={<Radio color="secondary" />}
                      label={<MText size={labelTextSize} />}
                      style={{ height: 'max-content' }}
                    />
                    <div>
                      <MText size={labelTextSize} text="Card's Validity" />
                      <MText
                        size={11}
                        text="Indicate how many day(s) the card will be valid when it is issued to the user"
                        className="mb-2"
                        colour={Colours.Gray1}
                      />
                      <div className="d-flex align-items-center">
                        <TextField
                          inputRef={daysInputRef}
                          disabled={validityBy !== ValidityBy.Days || isLive || isDeactivated}
                          variant="outlined"
                          size="small"
                          name={ValidityType.ValidityInDays}
                          value={inputs.validityInDays}
                          type="number"
                          error={inputs.validityInDays < 0}
                          helperText={inputs.validityInDays < 0 ? 'Validity in days at least 1' : ''}
                          onKeyDown={(event) => {
                            // on physical mobile-device softkeyboard, clicking on "Go" button won't submit the form
                            if (event.key === 'Enter') { event.preventDefault(); }
                          }}
                          placeholder="E.g. 365"
                          onChange={handleInput}
                          className="mr-2"
                          inputProps={{
                            min:   1,
                            style: {
                              width:     100,
                              fontSize:  labelTextSize,
                              fontStyle: IsEmpty(inputs.validityInDays) && 'italic',
                            },
                          }}
                        />
                        <MText text="days" size={labelTextSize} />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 my-1">
                  <div className="d-flex">
                    <FormControlLabel
                      value={ValidityBy.Date}
                      disabled={isLive || isDeactivated}
                      control={<Radio color="secondary" />}
                      label={<MText size={labelTextSize} />}
                      style={{ height: 'max-content' }}
                    />
                    <div>
                      <MText size={labelTextSize} text="Expiry date" />
                      <MText
                        size={11}
                        text={`${"Select card's expiry date"}`}
                        className="mb-2"
                        colour={Colours.Gray1}
                      />
                      <div className="d-flex align-items-center">
                        <MaterialDatePicker
                          disablePast
                          disabled={validityBy !== ValidityBy.Date || isLive || isDeactivated}
                          minDate={false}
                          onChange={(date = new Date()) => {
                            const formattedDate = moment(date).utcOffset('+08:00', true).endOf('day').format();
                              handleInput({ target: { name: ValidityType.ExpiryDate, value: formattedDate } });
                              setExpiryDateField(formattedDate.split('T')[0]);
                          }}
                          value={expiryDateField}
                          size="small"
                          variant="outlined"
                          className="mt-0"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </RadioGroup>
            </div>
          </div>

          <div className="row mb-4">
            <FormControlLabel
              control={(
                <Checkbox
                  checked={inputs.isAuthRequiredToIssue}
                  disabled={isDeactivated}
                  name="isAuthRequiredToIssue"
                  onChange={handleInput}
                  color="primary"
                />
              )}
              label="Is Auth Required to Issue"
            />
          </div>

          <div className="row mb-4">
            <div className="col-12 col-xl-5 pl-0">
              <Typography style={{ fontSize: 13 }}>
                Status
              </Typography>
              <RadioGroup row name="status" value={inputs.status} onChange={handleInput} defaultValue={Status.Draft}>
                {[Status.Draft, Status.Live, Status.Deactivated].map((status) => {
                  const isDisabled = checkStatusTransitionDisabled(status);

                  return (
                    <div className="mr-3">
                      <FormControlLabel
                        value={status}
                        control={<Radio color="secondary" disabled={isDisabled} />}
                        style={{ fontSize: 13 }}
                        label={status}
                      />
                    </div>
                  );
                })}
              </RadioGroup>
            </div>
          </div>

          <div className="row mb-2">
            <div className="col-12 col-xl-6 pl-0 d-flex justify-content-center">
              <Button
                disabled={isDeactivated}
                variant="contained"
                color="secondary"
                className={classes.saveButton}
                onClick={() => {
                  if ((isDraft && inputs.status === Status.Live) || ((isLive && inputs.status === Status.Deactivated))) {
                    setConfirmDialog({ open: true, context: inputs.status });
                  } else {
                    handleSave();
                  }
                }}
              >
                Save
              </Button>
            </div>
          </div>

          <Dialog open={confirmDialog.open} PaperProps={{ className: 'w-100 p-5' }}>
            <u><Typography style={{ fontSize: 16 }} className="mb-2">Confirmation</Typography></u>
            <Typography style={{ fontSize: 16 }} className="mb-3">
              Updating status to
              {' '}
              {confirmDialog.context === Status.Live && <b>{Status.Live}</b>}
              {confirmDialog.context === Status.Deactivated && <b><u>{Status.Deactivated.toUpperCase()}</u></b>}
              {' '}
              will have the following impact:
            </Typography>
            <ul className="ml-n4 ml-lg-0">
              {!IsEmpty(confirmDialog.context) && confirmationImpactTexts[confirmDialog.context].map((text, index) => (
                <li key={index}>
                  <Typography style={{ fontSize: 16 }} className="mb-3">{text}</Typography>
                </li>
              ))}
            </ul>
            <Typography style={{ fontSize: 16 }} className="mb-1">Proceed</Typography>
            <div className="d-flex align-items-center mb-4">
              <Checkbox
                onChange={(e) => setConfirmDialog({ confirmed: e.target.checked })}
                name="confirm"
                color="secondary"
                style={{ marginLeft: '-0.75rem' }}
              />
              <Typography
                style={{ fontSize: 16 }}
                className="ml-2 font-weight-bold"
              >
                I understand the impact and would like to proceed to update the loyalty card’s status to
                {' '}
                {confirmDialog.context === Status.Live && Status.Live}
                {confirmDialog.context === Status.Deactivated && Status.Deactivated.toUpperCase()}
              </Typography>
            </div>
            <div className="w-100 d-flex justify-content-end align-items-center">
              <Button
                variant="outlined"
                style={{ width: 100 }}
                className="mr-4"
                onClick={closeDialog}
              >
                <Typography style={{ fontSize: 14 }}>Cancel</Typography>
              </Button>
              <Button
                disabled={!confirmDialog.confirmed}
                variant="contained"
                color="secondary"
                style={{ width: 100 }}
                onClick={() => { setConfirmDialog({ open: false, confirmed: false, context: null }); handleSave(); }}
              >
                <Typography style={{ fontSize: 14 }}>{confirmDialog.context === Status.Deactivated ? 'Deactivate' : 'Update'}</Typography>
              </Button>
            </div>
          </Dialog>
        </form>
      </div>
    </>
  );
};

LoyaltyCardInfo.propTypes = {
  match: PropTypes.shape(Object).isRequired,
};

export default LoyaltyCardInfo;
